import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
  Chip,
  LinearProgress,
  Paper,
  useTheme,
} from '@mui/material';
import {
  FlashOn,
  TrendingDown,
  AccountBalance,
  Warning,
  CheckCircleOutline,
  ShowChart,
  Shield,
} from '@mui/icons-material';
import ExportModal from './ExportModal';

interface ScenarioPreset {
  id: string;
  name: string;
  badge: string;
  icon: React.ReactElement;
  description: string;
  gdv: number;
  irr: number;
  dscr: number;
  debtYield: number;
  noi: number;
  sdlt: number;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'STRESS';
  deltaIrr: string;
  deltaDscr: string;
}

const scenarioPresets: ScenarioPreset[] = [
  {
    id: 'base',
    name: 'Base Underwriting Case',
    badge: 'OPTIMAL',
    icon: <CheckCircleOutline sx={{ color: '#047857' }} />,
    description: 'Target UK underwriting case based on current market yields and agreed debt terms.',
    gdv: 52.4,
    irr: 18.2,
    dscr: 1.65,
    debtYield: 9.2,
    noi: 3.15,
    sdlt: 2.35,
    riskLevel: 'LOW',
    deltaIrr: 'BASE',
    deltaDscr: 'BASE',
  },
  {
    id: 'rate_hike',
    name: 'BoE Rate Spike (+150 bps)',
    badge: 'INTEREST RATE SHOCK',
    icon: <AccountBalance sx={{ color: '#1e40af' }} />,
    description: 'Bank of England Base Rate increases to 6.25%, raising senior debt SONIA margin.',
    gdv: 50.8,
    irr: 14.8,
    dscr: 1.28,
    debtYield: 8.4,
    noi: 3.15,
    sdlt: 2.35,
    riskLevel: 'MEDIUM',
    deltaIrr: '-3.4%',
    deltaDscr: '-0.37x',
  },
  {
    id: 'construction',
    name: 'Capex Slippage & Overrun',
    badge: 'DEVELOPMENT RISK',
    icon: <Warning sx={{ color: '#b45309' }} />,
    description: '12-month build slippage + 10% construction cost inflation across UK main contractor bids.',
    gdv: 52.4,
    irr: 12.5,
    dscr: 1.35,
    debtYield: 8.8,
    noi: 3.05,
    sdlt: 2.35,
    riskLevel: 'HIGH',
    deltaIrr: '-5.7%',
    deltaDscr: '-0.30x',
  },
  {
    id: 'yield_shift',
    name: 'Yield Expansion (+75 bps)',
    badge: 'VALUATION SHOCK',
    icon: <TrendingDown sx={{ color: '#be123c' }} />,
    description: 'Market-wide prime yield expansion from 4.75% to 5.50% at exit valuation.',
    gdv: 44.2,
    irr: 9.8,
    dscr: 1.42,
    debtYield: 8.1,
    noi: 3.15,
    sdlt: 2.35,
    riskLevel: 'HIGH',
    deltaIrr: '-8.4%',
    deltaDscr: '-0.23x',
  },
  {
    id: 'stagflation',
    name: 'UK Stagflation Stress Test',
    badge: 'COMBINED STRESS',
    icon: <Shield sx={{ color: '#881337' }} />,
    description: 'Combined macro stress test: Rate spike + yield shift + 15% vacancy buffer in year 2.',
    gdv: 41.8,
    irr: 5.4,
    dscr: 1.05,
    debtYield: 6.9,
    noi: 2.65,
    sdlt: 2.35,
    riskLevel: 'STRESS',
    deltaIrr: '-12.8%',
    deltaDscr: '-0.60x',
  },
];

const ScenarioSimulator: React.FC = () => {
  const theme = useTheme();
  const [selectedScenario, setSelectedScenario] = useState<ScenarioPreset>(scenarioPresets[0]);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [exportModalOpen, setExportModalOpen] = useState<boolean>(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleSelectScenario = (scenario: ScenarioPreset) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setIsCalculating(true);
    setSelectedScenario(scenario);
    timerRef.current = setTimeout(() => {
      setIsCalculating(false);
    }, 150);
  };

  const getRiskBadgeColor = (risk: ScenarioPreset['riskLevel']) => {
    switch (risk) {
      case 'LOW': return '#047857';
      case 'MEDIUM': return '#1e40af';
      case 'HIGH': return '#b45309';
      case 'STRESS': return '#be123c';
    }
  };

  return (
    <Box id="scenarios-section" sx={{ py: 12, bgcolor: '#ffffff', position: 'relative' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 7 }}>
          <Chip
            icon={<FlashOn sx={{ color: '#1e40af !important' }} />}
            label="AUTOMATED MACRO STRESS TESTING"
            sx={{
              bgcolor: 'rgba(30, 64, 175, 0.08)',
              border: '1px solid rgba(30, 64, 175, 0.2)',
              color: '#1e40af',
              px: 1,
              py: 0.5,
              mb: 2,
              fontWeight: 700,
              fontSize: '0.75rem',
              letterSpacing: '0.05em',
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 2,
              color: '#0f172a',
              fontSize: { xs: '2rem', md: '2.75rem' },
            }}
          >
            Automated Property Scenario Runs
          </Typography>
          <Typography
            variant="body1"
            sx={{
              maxWidth: 720,
              mx: 'auto',
              color: '#475569',
              fontSize: { xs: '1rem', md: '1.15rem' },
            }}
          >
            Simulate Bank of England interest rate hikes, inflation spikes, and exit cap rate shifts. Evaluate capital stack resilience automatically.
          </Typography>
        </Box>

        {/* Preset Buttons */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justify: 'center',
            gap: 1.5,
            mb: 5,
          }}
        >
          {scenarioPresets.map((sc) => {
            const isSelected = selectedScenario.id === sc.id;
            return (
              <Button
                key={sc.id}
                onClick={() => handleSelectScenario(sc)}
                variant={isSelected ? 'contained' : 'outlined'}
                startIcon={sc.icon}
                sx={{
                  py: 1.2,
                  px: 2.5,
                  borderRadius: 2.5,
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  bgcolor: isSelected ? '#1e40af' : '#ffffff',
                  borderColor: isSelected ? '#1e40af' : '#cbd5e1',
                  color: isSelected ? '#ffffff' : '#475569',
                  boxShadow: isSelected ? '0 4px 12px rgba(30, 64, 175, 0.2)' : 'none',
                  '&:hover': {
                    bgcolor: isSelected ? '#1d4ed8' : '#f8fafc',
                    borderColor: '#1e40af',
                  },
                }}
              >
                {sc.name}
              </Button>
            );
          })}
        </Box>

        {isCalculating && (
          <LinearProgress
            sx={{
              mb: 4,
              borderRadius: 2,
              bgcolor: 'rgba(30, 64, 175, 0.1)',
              '& .MuiLinearProgress-bar': { bgcolor: '#1e40af' },
            }}
          />
        )}

        {/* Dashboard Card */}
        <Card
          sx={{
            p: { xs: 3, md: 5 },
            border: `1px solid ${getRiskBadgeColor(selectedScenario.riskLevel)}`,
            background: '#ffffff',
            boxShadow: '0 10px 30px -5px rgba(15, 23, 42, 0.08)',
            borderRadius: 3.5,
          }}
        >
          {/* Top Info */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justify: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: 2,
              mb: 4,
              pb: 3,
              borderBottom: '1px solid #f1f5f9',
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Typography variant="h4" sx={{ color: '#0f172a', fontWeight: 800 }}>
                  {selectedScenario.name}
                </Typography>
                <Chip
                  label={selectedScenario.badge}
                  size="small"
                  sx={{
                    bgcolor: `${getRiskBadgeColor(selectedScenario.riskLevel)}12`,
                    color: getRiskBadgeColor(selectedScenario.riskLevel),
                    border: `1px solid ${getRiskBadgeColor(selectedScenario.riskLevel)}30`,
                    fontWeight: 700,
                  }}
                />
              </Box>
              <Typography variant="body1" sx={{ color: '#475569' }}>
                {selectedScenario.description}
              </Typography>
            </Box>

            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 600 }}>
                SCENARIO STATUS
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: '#047857',
                  fontFamily: '"JetBrains Mono", monospace',
                  fontWeight: 800,
                }}
              >
                Computed <span style={{ fontSize: '0.75rem', color: '#64748b' }}>(Full Underwriting Model)</span>
              </Typography>
            </Box>
          </Box>

          {/* Metrics Grid */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  bgcolor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 3,
                }}
              >
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'block', mb: 0.5 }}>
                  LEVERED IRR (NET)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      fontFamily: '"JetBrains Mono", monospace',
                      color: selectedScenario.irr >= 15 ? '#047857' : selectedScenario.irr >= 10 ? '#b45309' : '#be123c',
                    }}
                  >
                    {selectedScenario.irr}%
                  </Typography>
                  {selectedScenario.deltaIrr !== 'BASE' && (
                    <Typography
                      variant="caption"
                      sx={{ color: '#be123c', fontWeight: 700, fontFamily: 'monospace' }}
                    >
                      ({selectedScenario.deltaIrr})
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1 }}>
                  Target Benchmark: &gt;16.0%
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  bgcolor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 3,
                }}
              >
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'block', mb: 0.5 }}>
                  GROSS DEV VALUE (GDV)
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    fontFamily: '"JetBrains Mono", monospace',
                    color: '#0f172a',
                  }}
                >
                  £{selectedScenario.gdv}M
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1 }}>
                  UK Stamp Duty: £{selectedScenario.sdlt}M
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  bgcolor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 3,
                }}
              >
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'block', mb: 0.5 }}>
                  DEBT COVERAGE (DSCR)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      fontFamily: '"JetBrains Mono", monospace',
                      color: selectedScenario.dscr >= 1.35 ? '#1e40af' : selectedScenario.dscr >= 1.15 ? '#b45309' : '#be123c',
                    }}
                  >
                    {selectedScenario.dscr}x
                  </Typography>
                  {selectedScenario.deltaDscr !== 'BASE' && (
                    <Typography
                      variant="caption"
                      sx={{ color: '#be123c', fontWeight: 700, fontFamily: 'monospace' }}
                    >
                      ({selectedScenario.deltaDscr})
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1 }}>
                  Lender Covenant: &gt;1.20x
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  bgcolor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 3,
                }}
              >
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'block', mb: 0.5 }}>
                  STABILIZED DEBT YIELD
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    fontFamily: '"JetBrains Mono", monospace',
                    color: '#6d28d9',
                  }}
                >
                  {selectedScenario.debtYield}%
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1 }}>
                  Net NOI: £{selectedScenario.noi}M p.a.
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              bgcolor: selectedScenario.riskLevel === 'STRESS' ? '#fef2f2' : '#ecfdf5',
              border: `1px solid ${selectedScenario.riskLevel === 'STRESS' ? '#fecdd3' : '#a7f3d0'}`,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justify: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ShowChart sx={{ color: selectedScenario.riskLevel === 'STRESS' ? '#be123c' : '#047857', fontSize: 28 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#0f172a', fontWeight: 700 }}>
                  FiMod Capital Structure Analysis
                </Typography>
                <Typography variant="body2" sx={{ color: '#475569' }}>
                  {selectedScenario.riskLevel === 'LOW' && 'Asset satisfies debt covenants and private equity target return thresholds.'}
                  {selectedScenario.riskLevel === 'MEDIUM' && 'Satisfies covenants; mezzanine tranche requires monitoring.'}
                  {selectedScenario.riskLevel === 'HIGH' && 'Underwriting alert: Levered return drops below hurdle rate.'}
                  {selectedScenario.riskLevel === 'STRESS' && 'Covenant breach warning: Debt service coverage drops to 1.05x limit.'}
                </Typography>
              </Box>
            </Box>

            <Chip
              label="Export to Excel / PDF"
              onClick={() => setExportModalOpen(true)}
              sx={{
                bgcolor: '#ffffff',
                border: '1px solid #cbd5e1',
                color: '#0f172a',
                fontWeight: 700,
                cursor: 'pointer',
                '&:hover': { bgcolor: '#f8fafc', borderColor: '#047857' },
              }}
            />
          </Paper>
        </Card>
      </Container>

      {/* Export Modal Component */}
      <ExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
      />
    </Box>
  );
};

export default ScenarioSimulator;
